import { useEffect } from "react";

interface GenericEntityPreviewProps {
  entityType: string;
  entityId: string;
  versionNumber?: string;
  onReadyChange: (isReady: boolean) => void;
}

const ENTITY_TYPE_LABELS: Record<string, string> = {
  AB_TEST: "A/B Release",
  DATASET: "Dataset",
  INPUT_VARIABLE_SET: "Input Variable Set",
  REPORT: "Blueprint",
  SKILL_COLLECTION: "Skill Collection",
  SMART_TABLE: "Smart Table",
  TOOL: "Tool",
};

const formatEntityType = (entityType: string) =>
  ENTITY_TYPE_LABELS[entityType] ??
  entityType
    .split("_")
    .filter(Boolean)
    .map(part => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(" ");

export const GenericEntityPreview = ({
  entityType,
  entityId,
  versionNumber,
  onReadyChange,
}: GenericEntityPreviewProps) => {
  const versionLabel = versionNumber ? `v${versionNumber}` : "Latest";
  const label = formatEntityType(entityType);

  useEffect(() => {
    onReadyChange(true);
  }, [onReadyChange]);

  return (
    <div className="flex h-full w-full flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
      <div className="border-b border-slate-200 bg-white px-10 py-8">
        <div className="mb-5 flex items-center justify-between gap-4">
          <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-medium tracking-wide text-slate-700">
            {label.toUpperCase()}
          </span>
          <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-medium text-slate-700">
            {versionLabel}
          </span>
        </div>
        <h1 className="line-clamp-2 text-4xl font-semibold tracking-tight text-slate-950">
          {label} preview
        </h1>
        <p className="mt-4 max-w-2xl text-sm leading-6 text-slate-600">
          Deterministic preview surface for this {label.toLowerCase()}.
        </p>
      </div>

      <div className="grid min-h-0 flex-1 grid-cols-[1fr_320px] gap-6 bg-slate-50/60 px-10 py-8">
        <div className="flex min-h-0 flex-col justify-end overflow-hidden rounded-2xl border border-slate-200 bg-white p-6">
          <div className="space-y-4">
            <div className="h-3 w-3/4 rounded-full bg-slate-200" />
            <div className="h-3 w-1/2 rounded-full bg-slate-100" />
            <div className="mt-8 grid grid-cols-3 gap-3">
              <div className="h-24 rounded-xl border border-slate-200 bg-slate-50" />
              <div className="h-24 rounded-xl border border-slate-200 bg-slate-50" />
              <div className="h-24 rounded-xl border border-slate-200 bg-slate-50" />
            </div>
          </div>
        </div>

        <aside className="space-y-5 rounded-2xl border border-slate-200 bg-white p-5">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Entity type
            </p>
            <p className="mt-2 text-sm font-medium text-slate-800">{label}</p>
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Entity ID
            </p>
            <p className="mt-2 break-all text-sm font-medium text-slate-800">{entityId}</p>
          </div>
        </aside>
      </div>
    </div>
  );
};
